/** Workspace administration: members, roles, permission matrix, teams, board labels, rules. */
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { WorkspaceAppearance } from '@/entities/appearance';
import type { Workspace } from '@/entities/workspace';
import { api, queryKeys } from '@/shared/api';
import type { JournalQuery } from '@/shared/lib/journal-search';

import type {
  AccessRule,
  AuditEvent,
  BoardLabelEntry,
  CustomRole,
  HierarchyLayoutLevel,
  HierarchyResult,
  RoleHierarchy,
  DeletedWorkspace,
  Impact,
  MemberRole,
  NotificationPolicy,
  NotificationPreferences,
  PermissionMatrix,
  RuleSubjectKind,
  Team,
  WorkspaceMember,
} from './types';

const base = (workspaceId: string) => `/v1/workspaces/${workspaceId}`;

function useInvalidateWorkspace() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['collab'] });
    void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
    void client.invalidateQueries({ queryKey: queryKeys.boards.all });
  };
}

// --- workspaces -----------------------------------------------------------------------------

export function useCreateWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string | null }) =>
      api.post<Workspace>('/v1/workspaces', data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      void client.invalidateQueries({ queryKey: queryKeys.collab.myBoards });
    },
  });
}

export function useUpdateWorkspace(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name?: string;
      description?: string | null;
      /** The header tint; `null` clears it. */
      color?: string | null;
      /** Header + description surfaces; `null` resets both. */
      appearance?: WorkspaceAppearance | null;
      /** The succession fallback; `null` clears it (then the workspace owner). */
      default_board_owner_user_id?: string | null;
    }) => api.patch<Workspace>(base(workspaceId), data),
    onSuccess: (workspace) => {
      client.setQueryData(queryKeys.workspaces.detail(workspaceId), workspace);
      void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
    },
  });
}

/** Owner only: hand the workspace to another member, confirmed by the exact workspace name. */
export function useTransferOwnership(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: (data: { user_id: string; confirm_name: string }) =>
      api.post<Workspace>(`${base(workspaceId)}/transfer-ownership`, data),
    onSuccess: invalidate,
  });
}

/**
 * Soft-delete a TEAM workspace (`workspace.delete`; the exact name confirms). It disappears at
 * once and stays recoverable for 30 days.
 */
export function useDeleteWorkspace(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: { confirm_name: string }) => api.post(`${base(workspaceId)}/delete`, data),
    onSuccess: () => {
      client.removeQueries({ predicate: (q) => JSON.stringify(q.queryKey).includes(workspaceId) });
      void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      void client.invalidateQueries({ queryKey: queryKeys.boards.all });
      void client.invalidateQueries({ queryKey: queryKeys.collab.myBoards });
      void client.invalidateQueries({ queryKey: ['workspaces', 'deleted'] });
    },
  });
}

export function useDeletedWorkspaces() {
  return useQuery({
    queryKey: ['workspaces', 'deleted'],
    queryFn: ({ signal }) => api.get<DeletedWorkspace[]>('/v1/workspaces/deleted', { signal }),
  });
}

export function useRestoreWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => api.post<Workspace>(`${base(workspaceId)}/restore`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      void client.invalidateQueries({ queryKey: ['workspaces', 'deleted'] });
      void client.invalidateQueries({ queryKey: queryKeys.collab.myBoards });
    },
  });
}

/** Permanent deletion of a workspace that sits in the trash (the exact name confirms). */
export function usePurgeWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, confirm_name }: { workspaceId: string; confirm_name: string }) =>
      api.post(`${base(workspaceId)}/purge`, { confirm_name }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['workspaces', 'deleted'] });
    },
  });
}

// --- members --------------------------------------------------------------------------------

export function useWorkspaceMembers(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.members(workspaceId),
    queryFn: ({ signal }) => api.get<WorkspaceMember[]>(`${base(workspaceId)}/members`, { signal }),
    enabled,
  });
}

export function useUpdateMember(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({
      userId,
      ...data
    }: {
      userId: string;
    } & (
      | { role: MemberRole; custom_role_id?: undefined }
      | { custom_role_id: string; role?: undefined }
    )) =>
      // Exactly ONE role: a standard one or a custom one, never both.
      api.patch<WorkspaceMember>(`${base(workspaceId)}/members/${userId}`, data),
    onSuccess: invalidate,
  });
}

export function useRemoveMember(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`${base(workspaceId)}/members/${userId}`),
    onSuccess: invalidate,
  });
}

// --- custom roles and the matrix ------------------------------------------------------------

export function useCustomRoles(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.roles(workspaceId),
    queryFn: ({ signal }) => api.get<CustomRole[]>(`${base(workspaceId)}/roles`, { signal }),
    enabled,
  });
}

export interface CustomRoleInput {
  name: string;
  description?: string | null;
  color?: string | null;
  rank?: number;
  permissions?: string[];
  board_permissions?: string[];
}

export function useCreateCustomRole(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: (data: CustomRoleInput) => api.post<CustomRole>(`${base(workspaceId)}/roles`, data),
    onSuccess: invalidate,
  });
}

export function useUpdateCustomRole(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({ roleId, ...data }: { roleId: string } & Partial<CustomRoleInput>) =>
      api.patch<CustomRole>(`${base(workspaceId)}/roles/${roleId}`, data),
    onSuccess: invalidate,
  });
}

export function useDeleteCustomRole(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({
      roleId,
      ...decision
    }: {
      roleId: string;
      reassign_to_role_id?: string | null;
      clear?: boolean;
    }) => api.post(`${base(workspaceId)}/roles/${roleId}/delete`, decision),
    onSuccess: invalidate,
  });
}

export function useRoleHierarchy(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.hierarchy(workspaceId),
    queryFn: ({ signal }) =>
      api.get<RoleHierarchy>(`${base(workspaceId)}/role-hierarchy`, { signal }),
    enabled,
  });
}

/** Replaces the whole level layout; ``dry_run`` only previews the consequences. */
export function useSaveRoleHierarchy(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: (data: {
      expected_revision: number;
      levels: HierarchyLayoutLevel[];
      dry_run?: boolean;
      confirm_level_removal?: boolean;
    }) => api.put<HierarchyResult>(`${base(workspaceId)}/role-hierarchy`, data),
    onSuccess: (result) => {
      if (!result.dry_run) invalidate();
    },
  });
}

export function usePermissionMatrix(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.matrix(workspaceId),
    queryFn: ({ signal }) =>
      api.get<PermissionMatrix>(`${base(workspaceId)}/permissions`, { signal }),
    enabled,
  });
}

export function useSetRolePermissions(workspaceId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({ role, permissions }: { role: MemberRole; permissions: string[] }) =>
      api.put<PermissionMatrix>(`${base(workspaceId)}/permissions/${role}`, { permissions }),
    onSuccess: (matrix) => {
      client.setQueryData(queryKeys.collab.matrix(workspaceId), matrix);
      invalidate();
    },
  });
}

// --- teams ----------------------------------------------------------------------------------

export function useTeams(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.teams(workspaceId),
    queryFn: ({ signal }) => api.get<Team[]>(`${base(workspaceId)}/teams`, { signal }),
    enabled,
  });
}

export function useCreateTeam(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: (data: { name: string; description?: string | null }) =>
      api.post<Team>(`${base(workspaceId)}/teams`, data),
    onSuccess: invalidate,
  });
}

export function useUpdateTeam(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({
      teamId,
      ...data
    }: {
      teamId: string;
      name?: string;
      description?: string | null;
    }) => api.patch<Team>(`${base(workspaceId)}/teams/${teamId}`, data),
    onSuccess: invalidate,
  });
}

/** Appoint, replace or clear (`userId: null`) the leader of a team. */
export function useSetTeamLeader(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string | null }) =>
      api.put<Team>(`${base(workspaceId)}/teams/${teamId}/leader`, { user_id: userId }),
    onSuccess: invalidate,
  });
}

/** Deleting a team can revoke access: preview the impact first (`dryRun`). */
export function useDeleteTeam(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({ teamId, dryRun = false }: { teamId: string; dryRun?: boolean }) =>
      api.delete<Impact>(`${base(workspaceId)}/teams/${teamId}`, {
        query: { dry_run: dryRun },
      }),
    onSuccess: (_impact, { dryRun }) => {
      if (!dryRun) invalidate();
    },
  });
}

export function useSetTeamMember(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({
      teamId,
      userId,
      present,
      dryRun = false,
    }: {
      teamId: string;
      userId: string;
      present: boolean;
      dryRun?: boolean;
    }) => {
      const path = `${base(workspaceId)}/teams/${teamId}/members/${userId}`;
      const options = { query: { dry_run: dryRun } };
      return present
        ? api.put<{ team: Team | null; impact: Impact }>(path, undefined, options)
        : api.delete<{ team: Team | null; impact: Impact }>(path, options);
    },
    onSuccess: (_result, { dryRun }) => {
      if (!dryRun) invalidate();
    },
  });
}

// --- board labels ---------------------------------------------------------------------------

export function useBoardLabelCatalog(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.boardLabels(workspaceId),
    queryFn: ({ signal }) =>
      api.get<BoardLabelEntry[]>(`${base(workspaceId)}/board-labels`, { signal }),
    enabled,
  });
}

export function useCreateBoardLabel(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: (data: { name: string; color: string }) =>
      api.post<BoardLabelEntry>(`${base(workspaceId)}/board-labels`, data),
    onSuccess: invalidate,
  });
}

export function useUpdateBoardLabel(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({ labelId, ...data }: { labelId: string; name?: string; color?: string }) =>
      api.patch<BoardLabelEntry>(`${base(workspaceId)}/board-labels/${labelId}`, data),
    onSuccess: invalidate,
  });
}

/** Persist the catalog order (presentation only; it never changes access). */
export function useReorderBoardLabels(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (labelIds: string[]) =>
      api.put<BoardLabelEntry[]>(`${base(workspaceId)}/board-labels/order`, {
        label_ids: labelIds,
      }),
    onSuccess: (labels) => {
      client.setQueryData(queryKeys.collab.boardLabels(workspaceId), labels);
    },
  });
}

/** The boards (that the viewer may see) carrying one label. */
export function useLabelBoardIds(workspaceId: string, labelId: string | null) {
  return useQuery({
    queryKey: [...queryKeys.collab.boardLabels(workspaceId), labelId, 'boards'],
    queryFn: ({ signal }) =>
      api.get<string[]>(`${base(workspaceId)}/board-labels/${labelId}/boards`, { signal }),
    enabled: labelId !== null,
  });
}

export interface AssignLabelResult {
  changed_board_ids: string[];
  skipped_board_ids: string[];
  impact: Impact;
}

/** Add / remove one label on several boards at once (preview with `dryRun`). */
export function useAssignBoardLabel(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({
      labelId,
      boardIds,
      remove = false,
      dryRun = false,
    }: {
      labelId: string;
      boardIds: string[];
      remove?: boolean;
      dryRun?: boolean;
    }) =>
      api.post<AssignLabelResult>(`${base(workspaceId)}/board-labels/${labelId}/boards`, {
        board_ids: boardIds,
        remove,
        dry_run: dryRun,
      }),
    onSuccess: (_result, { dryRun }) => {
      if (!dryRun) invalidate();
    },
  });
}

export function useDeleteBoardLabel(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({ labelId, dryRun = false }: { labelId: string; dryRun?: boolean }) =>
      api.delete<Impact>(`${base(workspaceId)}/board-labels/${labelId}`, {
        query: { dry_run: dryRun },
      }),
    onSuccess: (_impact, { dryRun }) => {
      if (!dryRun) invalidate();
    },
  });
}

// --- access rules ---------------------------------------------------------------------------

export function useAccessRules(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.rules(workspaceId),
    queryFn: ({ signal }) => api.get<AccessRule[]>(`${base(workspaceId)}/access-rules`, { signal }),
    enabled,
  });
}

export interface AccessRuleInput {
  subject_kind: RuleSubjectKind;
  team_id?: string | null;
  subject_role?: MemberRole | null;
  custom_role_id?: string | null;
  granted_role: MemberRole;
  board_id?: string | null;
  label_ids?: string[];
  label_match?: 'any' | 'all';
  dry_run?: boolean;
}

export function useCreateAccessRule(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: (data: AccessRuleInput) =>
      api.post<{ rule: AccessRule | null; impact: Impact }>(
        `${base(workspaceId)}/access-rules`,
        data,
      ),
    onSuccess: (_result, data) => {
      if (!data.dry_run) invalidate();
    },
  });
}

export function useDeleteAccessRule(workspaceId: string) {
  const invalidate = useInvalidateWorkspace();
  return useMutation({
    mutationFn: ({ ruleId, dryRun = false }: { ruleId: string; dryRun?: boolean }) =>
      api.delete<Impact>(`${base(workspaceId)}/access-rules/${ruleId}`, {
        query: { dry_run: dryRun },
      }),
    onSuccess: (_impact, { dryRun }) => {
      if (!dryRun) invalidate();
    },
  });
}

// --- audit ----------------------------------------------------------------------------------

const AUDIT_PAGE = 50;

/** Newest first; "load more" walks back with the keyset cursor of the last event. */
export function useWorkspaceAudit(workspaceId: string, enabled = true, search: JournalQuery = {}) {
  const types = search.types ?? [];
  return useInfiniteQuery<
    AuditEvent[],
    Error,
    InfiniteData<AuditEvent[], string | null>,
    readonly unknown[],
    string | null
  >({
    // Longer than the registry key: a realtime hint invalidating the prefix refreshes searches too.
    queryKey: [...queryKeys.collab.audit(workspaceId), search.q ?? '', types],
    initialPageParam: null,
    queryFn: ({ signal, pageParam }) =>
      api.get<AuditEvent[]>(`${base(workspaceId)}/audit`, {
        signal,
        query: { limit: AUDIT_PAGE, before: pageParam, q: search.q },
        ...(types.length > 0 && { repeat: { types } }),
      }),
    getNextPageParam: (last) => (last.length === AUDIT_PAGE ? (last.at(-1)?.id ?? null) : null),
    // typing a search keeps the list on screen until the answer arrives
    placeholderData: keepPreviousData,
    enabled,
  });
}

// --- notification policy and preferences ------------------------------------------------------

export function useNotificationPolicy(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.collab.notificationPolicy(workspaceId)],
    queryFn: ({ signal }) =>
      api.get<NotificationPolicy>(`${base(workspaceId)}/notification-policy`, { signal }),
    enabled,
  });
}

export function useSetNotificationPolicy(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (categories: Record<string, boolean>) =>
      api.put<NotificationPolicy>(`${base(workspaceId)}/notification-policy`, { categories }),
    onSuccess: (policy) => {
      client.setQueryData(queryKeys.collab.notificationPolicy(workspaceId), policy);
      void client.invalidateQueries({
        queryKey: queryKeys.collab.notificationPreferences(workspaceId),
      });
    },
  });
}

export function useNotificationPreferences(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.notificationPreferences(workspaceId),
    queryFn: ({ signal }) =>
      api.get<NotificationPreferences>(`${base(workspaceId)}/notification-preferences`, {
        signal,
      }),
    enabled,
  });
}

export function useSetNotificationPreferences(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (categories: Record<string, boolean>) =>
      api.put<NotificationPreferences>(`${base(workspaceId)}/notification-preferences`, {
        categories,
      }),
    onSuccess: (preferences) => {
      client.setQueryData(queryKeys.collab.notificationPreferences(workspaceId), preferences);
    },
  });
}

/** A rule "this team / role gets this role on the databases or notes carrying these labels". */
export interface LabelAccessRule {
  id: string;
  resource_kind: 'database' | 'document';
  subject_kind: RuleSubjectKind;
  team_id: string | null;
  subject_role: MemberRole | null;
  custom_role_id: string | null;
  granted_role: MemberRole;
  label_ids: string[];
  label_match: 'any' | 'all';
  /** An inactive rule stays listed (labels, recipient, role) but grants nothing. */
  is_active: boolean;
  created_at: string;
  /** How many resources carry the labels right now (a count - names are never listed). */
  matching_count: number;
}

export interface LabelAccessRuleInput {
  resource_kind: 'database' | 'document';
  subject_kind: RuleSubjectKind;
  team_id?: string;
  subject_role?: MemberRole;
  custom_role_id?: string;
  granted_role: MemberRole;
  label_ids: string[];
  label_match: 'any' | 'all';
  dry_run: boolean;
}

const labelRulesKey = (workspaceId: string) => ['workspaces', workspaceId, 'label-rules'] as const;

export function useLabelAccessRules(workspaceId: string) {
  return useQuery({
    queryKey: labelRulesKey(workspaceId),
    queryFn: ({ signal }) =>
      api.get<LabelAccessRule[]>(`${base(workspaceId)}/label-access-rules`, { signal }),
  });
}

export function useCreateLabelAccessRule(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: LabelAccessRuleInput) =>
      api.post<{ rule: LabelAccessRule | null; matching_count: number }>(
        `${base(workspaceId)}/label-access-rules`,
        data,
      ),
    onSuccess: (_result, data) => {
      if (!data.dry_run) void client.invalidateQueries({ queryKey: labelRulesKey(workspaceId) });
    },
  });
}

/** Switch a rule off (nothing is granted, the rule is kept) or on again. */
export function useSetLabelAccessRuleActive(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, active }: { ruleId: string; active: boolean }) =>
      api.patch<LabelAccessRule>(`${base(workspaceId)}/label-access-rules/${ruleId}`, {
        is_active: active,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: labelRulesKey(workspaceId) }),
  });
}

export function useDeleteLabelAccessRule(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => api.delete(`${base(workspaceId)}/label-access-rules/${ruleId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: labelRulesKey(workspaceId) }),
  });
}
